import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, Check, Share2, RotateCcw } from 'lucide-react';

// Images
const IMAGES = {
  kristen: "https://customer-assets-m6fa6gv7.emergentagent.net/job_20074a01-0522-4acf-a362-5ce15b30849a/artifacts/dcc883a840cdcc4a_kristen.jpg",
  team: "https://customer-assets-m6fa6gv7.emergentagent.net/job_20074a01-0522-4acf-a362-5ce15b30849a/artifacts/869677fc1563a6d9_team-photo.jpg",
};

// Videos based on Q7 answer - YouTube embeds
const VIDEOS = {
  comp: {
    src: "https://www.youtube-nocookie.com/embed/Epjl5qLvZEc?rel=0&modestbranding=1&playsinline=1",
    title: "how the compensation works",
    caption: "1 min · from my ready to run breakdown · income isn't guaranteed.",
    captionLink: { text: "income disclosure", url: "https://monatglobal.com/income-disclosure-statement/" },
    phrase: "how the compensation works"
  },
  day_to_day: {
    src: "https://www.youtube-nocookie.com/embed/hp3lg5c26Os?rel=0&modestbranding=1&playsinline=1",
    title: "what i actually do day to day",
    caption: "30 sec · from my ready to run breakdown",
    phrase: "what the day-to-day actually looks like"
  },
  support: {
    src: "https://www.youtube-nocookie.com/embed/gAPJ8vOJA-w?rel=0&modestbranding=1&playsinline=1",
    title: "the training & support you'd have",
    caption: "1 min · from my ready to run breakdown",
    phrase: "the training & support you'd have"
  },
  company: {
    src: "https://www.youtube-nocookie.com/embed/HHv7Tp7N-AI?rel=0&modestbranding=1&playsinline=1",
    title: "the company & what it sells",
    caption: "1 min · from my ready to run breakdown",
    phrase: "what the company is & what it sells"
  }
};

// Order for showing all videos
const ALL_VIDEOS_ORDER = ['comp', 'day_to_day', 'support', 'company'];

// SMS Links
const SMS_RUNIT = "sms:+15418713311?body=RUN%20IT";
const SMS_CONSULT = "sms:+15418713311?body=CONSULT";

// External Links
const LINKS = {
  runIt: "https://kristenhinman.com/run-it?utm_source=quiz&utm_content=how",
  tryProducts: "https://leveragequiz.com/try-products?utm_source=quiz&utm_content=products",
  instagram: "https://www.instagram.com/iamkristenhinman/",
  incomeDisclosure: "https://monatglobal.com/income-disclosure-statement/",
};

// Questions data
const QUESTIONS = [
  {
    id: 'career',
    question: "how would you describe where you are in your career?",
    answers: [
      { label: "i'm established & people know me for what i do", value: "established" },
      { label: "i've built a solid reputation & i'm still growing", value: "growing" },
      { label: "i'm figuring out what i want to be known for", value: "figuring_out" },
    ]
  },
  {
    id: 'work',
    question: "how do you feel about the work you do?",
    answers: [
      { label: "i love it. i'm not looking to change careers.", value: "love_it" },
      { label: "i like what i do, but i'd love more options.", value: "more_options" },
      { label: "honestly, i'm ready for something different.", value: "ready_for_different" },
    ]
  },
  {
    id: 'more_money',
    question: "when you want to make more money, what usually has to happen?",
    answers: [
      { label: "i work more hours or take on more responsibility", value: "more_hours" },
      { label: "i ask for a raise, increase my rates or look for a better-paying opportunity", value: "raise_rates" },
      { label: "i have to find another way to bring money in", value: "another_way" },
      { label: "i'm happy with what i'm earning right now", value: "happy" },
    ]
  },
  {
    id: 'time_off',
    question: "if you took two weeks off, what would happen to your income?",
    answers: [
      { label: "i'd still get paid, but my time off is limited", value: "paid_limited" },
      { label: "i'd lose income or have to catch up when i got back", value: "lose_income" },
      { label: "my income would keep coming in without much changing", value: "keeps_coming" },
    ]
  },
  {
    id: 'gift',
    question: "which of these comes naturally to you?",
    answers: [
      { label: "bringing people together & making connections", value: "connect" },
      { label: "mentoring people & helping them grow", value: "mentor" },
      { label: "sharing things i believe in & getting people excited about them", value: "share" },
      { label: "honestly, a little of all three", value: "all" },
    ]
  },
  {
    id: 'considered',
    question: "have you ever considered adding something where your leadership could create another way to earn?",
    answers: [
      { label: "no, but now i'm curious", value: "curious" },
      { label: "i've thought about it, but wouldn't know where to start", value: "where_start" },
      { label: "yes, but i'd need to understand the business model", value: "business_model" },
      { label: "i've tried something like that before", value: "tried" },
    ]
  },
  {
    id: 'know',
    question: "before i'd tell you to seriously consider this, what would you need to see?",
    answers: [
      { label: "how the compensation plan works", value: "comp" },
      { label: "what i'd actually be doing day to day", value: "day_to_day" },
      { label: "what kind of training & support i'd have", value: "support" },
      { label: "what the company is & what it sells", value: "company" },
      { label: "honestly, all of the above", value: "all" },
    ]
  },
  {
    id: 'want',
    question: "if you could create more options for yourself, what would you want most?",
    multiSelect: true,
    answers: [
      { label: "more time with my family", value: "family" },
      { label: "more financial breathing room", value: "breathing_room" },
      { label: "more flexibility with my schedule", value: "flexibility" },
      { label: "another way to earn that isn't tied directly to my hours", value: "another_way" },
      { label: "the freedom to make my career look different down the road", value: "different" },
    ]
  },
];

// Track pixel events
const trackEvent = (eventName, params = {}) => {
  if (typeof window !== 'undefined' && window.fbq) {
    if (eventName === 'ViewContent' || eventName === 'Lead') {
      window.fbq('track', eventName, params);
    } else {
      window.fbq('trackCustom', eventName, params);
    }
  }
};

// Determine the "take" based on answers
const determineTake = (answers) => {
  const { more_money, time_off, career, know, considered } = answers;
  
  // Check in order, first match wins
  if (more_money === 'happy' && time_off === 'keeps_coming') {
    return 'not_now';
  }
  if (career === 'figuring_out' || know === 'company') {
    return 'products_first';
  }
  if (considered === 'business_model' || considered === 'tried' || know === 'comp' || know === 'all') {
    return 'full_model';
  }
  return 'closer_look';
};

const GIFT_PHRASES = {
  connect: 'you bring people together',
  mentor: 'you mentor people & help them grow',
  share: 'you get people excited about what you believe in',
  all: 'you do a little of all three: connecting, mentoring & sharing',
};

// Build the "why" paragraph
const buildWhyParagraph = (take, answers, config) => {
  const giftPhrase = config.giftPhrases[answers.gift] || '';
  
  if (take === 'not_now') {
    return "you're happy with what you're earning & your income keeps coming in when you step away. that's a great place to be, & i wouldn't mess with it.";
  }
  
  if (take === 'products_first') {
    if (answers.career === 'figuring_out') {
      return config.figuringOutWhy(giftPhrase);
    }
    return `you'd want to know what the company is & what it sells before anything else. smart. and since ${giftPhrase}, you'll know pretty fast if you'd stand behind the products.`;
  }
  
  // closer_look or full_model
  const careerPart = answers.career === 'established' 
    ? "people already know you for what you do" 
    : config.careerGrowing;
  
  let incomeAddition = '';
  if (answers.time_off === 'lose_income') {
    incomeAddition = config.loseIncome;
  } else if (answers.time_off === 'paid_limited') {
    incomeAddition = " and right now, your time off is limited.";
  }
  
  return `${careerPart}, and ${giftPhrase}. those skills matter in this business.${incomeAddition}`;
};

// Build the "before you decide" paragraph
const buildBeforeYouDecide = (know, videos) => {
  const knowMap = {
    comp: "how the compensation works",
    day_to_day: "what the day-to-day actually looks like",
    support: "the training & support you'd have",
    company: "what the company is & what it sells",
    all: "how the compensation works, what the day-to-day looks like & where the products fit in"
  };
  return `before you decide anything, i'd want you to see ${videos[know]?.phrase || knowMap[know] || knowMap.all}.`;
};

// Get want line
const WANT_LINES = {
  family: { text: "more time with your family", emoji: "🤍" },
  breathing_room: { text: "more financial breathing room", emoji: "" },
  flexibility: { text: "more flexibility with your schedule", emoji: "☀️" },
  another_way: { text: "another way to earn that isn't tied to your hours", emoji: "" },
  different: { text: "the freedom to make your career look different down the road", emoji: "" },
};

const getWantLine = (want, take, wantLines, wantAdditions) => {
  
  // Handle comma-separated multiple selections
  const wantValues = want ? want.split(',') : ['family'];
  
  // Build combined text from all selections
  const selectedTexts = wantValues.map(w => wantLines[w]?.text || '').filter(Boolean);
  const combinedText = selectedTexts.length > 1 
    ? selectedTexts.slice(0, -1).join(', ') + ' & ' + selectedTexts[selectedTexts.length - 1]
    : selectedTexts[0] || wantLines.family.text;
  
  // Get first emoji if any
  const emoji = wantValues.find(w => wantLines[w]?.emoji)
    ? wantLines[wantValues.find(w => wantLines[w]?.emoji)]?.emoji 
    : '';
  
  // Special additions for specific wants (only if single selection)
  let additionalText = '';
  if (wantValues.length === 1) {
    additionalText = wantAdditions[wantValues[0]] || '';
  }
  
  const fullText = `you said ${combinedText}.${additionalText}`;
  
  if (take === 'not_now') {
    // For not_now, use first sentence only + "keep that in your back pocket"
    return { text: `you said ${combinedText}. keep that in your back pocket.`, emoji };
  }
  
  return { text: fullText, emoji };
};

// Get card descriptions based on know value
const CARD_DESCRIPTIONS = {
    comp: {
      talk: "text me & i'll walk you through how the compensation plan works.",
      howItWorks: "the whole breakdown, including how the compensation plan works & what it costs to start (about $300).",
      products: "try the products first. answer 3 quick questions & i'll send you the routine i'd use. (a starting cart is usually $110 to $150.)"
    },
    day_to_day: {
      talk: "text me & i'll tell you what i actually do in a normal week.",
      howItWorks: "the whole breakdown, including what a normal week looks like.",
      products: "try the products first. answer 3 quick questions & i'll send you the routine i'd use. (a starting cart is usually $110 to $150.)"
    },
    support: {
      talk: "text me & i'll show you the training, the team & the group chat that answers in about five minutes 😁",
      howItWorks: "the whole breakdown, including the training & support you'd have.",
      products: "try the products first. answer 3 quick questions & i'll send you the routine i'd use. (a starting cart is usually $110 to $150.)"
    },
    company: {
      talk: "text me & i'll tell you about the company & what it sells.",
      howItWorks: "the whole breakdown, including the company & what it sells.",
      products: "see what the company sells for yourself. answer 3 quick questions & i'll send you the routine i'd use. (a starting cart is usually $110 to $150.)"
    },
    all: {
      talk: "text me & we'll figure out if it fits your life. no pitch, just a conversation.",
      howItWorks: "the whole thing in one place: the company, the compensation plan, the training & what it costs to start (about $300).",
      products: "try the products first. answer 3 quick questions & i'll send you the routine i'd use. (a starting cart is usually $110 to $150.)"
    }
};

const CARD_ORDERS = {
  closer_look: ['talk', 'howItWorks', 'products'],
  full_model: ['howItWorks', 'talk', 'products'],
  products_first: ['products', 'howItWorks', 'talk'],
};

// Config for the general quiz at "/" (practitioner page overrides parts of this)
export const GENERAL_CONFIG = {
  version: 'general',
  storageKey: 'leverageQuizState',
  meta: null,
  intro: {
    eyebrow: 'an 8-question quiz ✦',
    headlinePrefix: 'could your experience be',
    byline: "hi, i'm kristen. i work with women who are great at what they do & still only get paid when they show up.",
    paragraph: "this quiz is my honest take on whether adding a way to get paid that isn't tied to your hours makes sense for you. (not everyone should, & i'd rather tell you now.)",
  },
  questions: QUESTIONS,
  videos: VIDEOS,
  allVideosLine: { text: 'watch the full 26-minute breakdown →', url: 'https://kristenhinman.com/run-it?utm_source=quiz&utm_content=full_breakdown' },
  giftPhrases: GIFT_PHRASES,
  figuringOutWhy: (giftPhrase) => `you're still figuring out what you want to be known for, & ${giftPhrase}. i'd get to know the products first & see how they feel to you.`,
  careerGrowing: "you've built a solid reputation",
  loseIncome: " and right now, your income depends on you showing up.",
  teamLine: "and you don't have to give up your work for this, it gets to be both. you don't need to build more, you need to get paid more for what you've already built, so i'll hand you everything i use.",
  wantLines: WANT_LINES,
  wantAdditions: {
    another_way: " same reason heidi on my team started (she's paying off debt).",
    different: ' same reason michelle on my team started.',
  },
  talkCard: { buttonText: 'text me RUN IT', link: SMS_RUNIT, ctaType: 'runit_sms', newTab: false },
  productsButtonText: 'find my hair routine',
  allVideosIntro: "before you decide anything, i'd want you to see all of it, so here's me explaining each piece, about a minute each.",
  cardDescriptions: CARD_DESCRIPTIONS,
  cardOrders: CARD_ORDERS,
  howItWorksTitle: 'let me show you how it works',
  links: { howItWorks: LINKS.runIt, tryProducts: LINKS.tryProducts },
  share: {
    title: 'could your experience be making you more money?',
    text: '8 quick questions & my honest take on whether this makes sense for you right now.',
    url: 'https://leveragequiz.com',
  },
  finePrintExtra: '',
};

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function LeverageQuizPage({ config = GENERAL_CONFIG }) {
  const QUESTIONS = config.questions;
  const VIDEOS = config.videos;
  const postAnalytics = (event) => {
    fetch(`${API_URL}/api/leverage/analytics`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event, quiz_version: config.version })
    }).catch(() => {});
  };

  const [saved] = useState(() => JSON.parse(sessionStorage.getItem(config.storageKey) || '{}'));
  // Screen state: 'intro' | 'quiz' | 'email' | 'result'
  const [screen, setScreen] = useState(saved.screen || 'intro');
  const [currentQuestion, setCurrentQuestion] = useState(saved.currentQuestion ?? 0);
  const [answers, setAnswers] = useState(saved.answers || {});
  const [firstName, setFirstName] = useState(saved.firstName || '');
  const [email, setEmail] = useState(saved.email || '');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(saved.result || null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [multiSelectAnswers, setMultiSelectAnswers] = useState([]);
  const [copied, setCopied] = useState(false);
  const [videoPlayed, setVideoPlayed] = useState(false);

  useEffect(() => {
    if (!config.meta) return;
    document.title = config.meta.title;
    document.querySelectorAll('meta[name="description"], meta[property="og:description"], meta[name="twitter:description"]')
      .forEach((el) => el.setAttribute('content', config.meta.description));
    document.querySelectorAll('meta[property="og:title"], meta[name="twitter:title"]')
      .forEach((el) => el.setAttribute('content', config.meta.title));
  }, [config.meta]);

  // Save state to sessionStorage on change
  useEffect(() => {
    sessionStorage.setItem(config.storageKey, JSON.stringify({
      screen,
      currentQuestion,
      answers,
      firstName,
      email,
      result,
    }));
  }, [screen, currentQuestion, answers, firstName, email, result]);

  // Track intro view
  useEffect(() => {
    if (screen === 'intro') {
      trackEvent('ViewContent');
      // Track intro view for analytics
      postAnalytics('intro_view');
    }
  }, [screen]);

  const startQuiz = () => {
    trackEvent('QuizStart');
    postAnalytics('quiz_start');
    setScreen('quiz');
    setCurrentQuestion(0);
  };

  const handleAnswer = (value) => {
    const question = QUESTIONS[currentQuestion];
    
    // Handle multi-select questions (Q8)
    if (question.multiSelect) {
      const newMultiSelect = multiSelectAnswers.includes(value)
        ? multiSelectAnswers.filter(v => v !== value)
        : [...multiSelectAnswers, value];
      setMultiSelectAnswers(newMultiSelect);
      return; // Don't auto-advance for multi-select
    }
    
    // Single select behavior
    setSelectedAnswer(value);
    
    // Track question completion
    postAnalytics(`question_${currentQuestion + 1}`);
    
    // Delay for visual feedback
    setTimeout(() => {
      const newAnswers = { ...answers, [QUESTIONS[currentQuestion].id]: value };
      setAnswers(newAnswers);
      setSelectedAnswer(null);
      
      if (currentQuestion < QUESTIONS.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
      } else {
        // Track email screen view
        postAnalytics('email_view');
        setScreen('email');
      }
    }, 250);
  };

  // Handle multi-select continue button
  const handleMultiSelectContinue = () => {
    if (multiSelectAnswers.length === 0) return;
    
    // Track question completion
    postAnalytics(`question_${currentQuestion + 1}`);
    
    // Save all selected answers as array (joined by comma for storage)
    const newAnswers = { ...answers, [QUESTIONS[currentQuestion].id]: multiSelectAnswers.join(',') };
    setAnswers(newAnswers);
    setMultiSelectAnswers([]);
    
    // Track email screen view and move to email
    postAnalytics('email_view');
    setScreen('email');
  };

  const goBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setMultiSelectAnswers([]); // Reset multi-select when going back
    } else {
      setScreen('intro');
    }
  };

  // Track video play
  const handleVideoPlay = (take, knowAnswer) => {
    if (!videoPlayed) {
      trackEvent('ResultVideoPlay', { take, know: knowAnswer });
      setVideoPlayed(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!firstName.trim() || !email.trim()) {
      setError('need your first name & email for this part');
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('need your first name & email for this part');
      return;
    }
    
    setIsSubmitting(true);
    
    // Determine the take
    const take = determineTake(answers);
    
    // Get UTM params
    const urlParams = new URLSearchParams(window.location.search);
    const utmData = {
      utm_source: urlParams.get('utm_source') || '',
      utm_medium: urlParams.get('utm_medium') || '',
      utm_campaign: urlParams.get('utm_campaign') || '',
      utm_content: urlParams.get('utm_content') || '',
    };
    
    try {
      const response = await fetch(`${API_URL}/api/leverage/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: firstName.trim(),
          email: email.trim(),
          take,
          ...answers,
          ...utmData,
          quiz_version: config.version,
        }),
      });
      
      if (!response.ok) {
        throw new Error('Submission failed');
      }
      
      // Track Lead event
      trackEvent('Lead', { take });
      
      // Track completion
      postAnalytics('completion');
      
      setResult({ take, answers });
      setScreen('result');
    } catch (err) {
      console.error('Submit error:', err);
      // Still show result even if API fails
      setResult({ take, answers });
      setScreen('result');
    } finally {
      setIsSubmitting(false);
    }
  };

  const trackCTAClick = (ctaType) => {
    trackEvent('ResultCTA', { take: result?.take, cta: ctaType });
    postAnalytics(`cta_${ctaType}`);
  };

  const handleShare = async () => {
    const shareData = config.share;
    
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        // User cancelled or error
      }
    } else {
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(config.share.url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        // Fallback failed
      }
    }
  };

  const retakeQuiz = () => {
    sessionStorage.removeItem(config.storageKey);
    setScreen('intro');
    setCurrentQuestion(0);
    setAnswers({});
    setFirstName('');
    setEmail('');
    setResult(null);
    setError('');
  };

  // Render intro screen
  const renderIntro = () => (
    <div className="min-h-screen bg-oat relative overflow-hidden">
      {/* Soft lime glows */}
      <div className="absolute top-0 right-0 w-80 h-80 bg-lime/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-lime/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
      
      <div className="relative z-10 min-h-screen flex items-center justify-center px-5 py-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-lg p-8 pt-9 max-w-[460px] w-full"
        >
          {/* Eyebrow */}
          <p className="text-moss-green text-xs uppercase tracking-wider mb-4 font-hanken">
            {config.intro.eyebrow}
          </p>
          
          {/* Headline */}
          <h1 className="font-anton text-[34px] sm:text-[46px] leading-[1.1] text-ink lowercase mb-6">
            {config.intro.headlinePrefix}{' '}
            <span className="bg-lime text-ink-deep px-2 py-0.5 rounded-md inline-block">
              making you more money?
            </span>
          </h1>
          
          {/* Byline with photo */}
          <div className="flex items-start gap-3 mb-5">
            <img
              src={IMAGES.kristen}
              alt="Kristen Hinman"
              className="w-12 h-12 rounded-full object-cover ring-2 ring-lime flex-shrink-0"
              style={{ objectPosition: 'center 20%' }}
            />
            <p className="font-hanken text-[16px] text-ink leading-snug">
              {config.intro.byline}
            </p>
          </div>
          
          {/* Body paragraph */}
          <p className="font-hanken text-[17px] text-ink/70 mb-8 leading-relaxed">
            {config.intro.paragraph}
          </p>
          
          {/* CTA Button */}
          <button
            onClick={startQuiz}
            className="w-full bg-ink text-oat font-hanken font-bold text-lg py-4 rounded-full hover:bg-ink/90 transition-all"
            data-testid="start-quiz-btn"
          >
            take the quiz <ArrowRight className="inline-block w-5 h-5 ml-1" />
          </button>
        </motion.div>
      </div>
    </div>
  );

  // Render question screen
  const renderQuiz = () => {
    const question = QUESTIONS[currentQuestion];
    const progress = ((currentQuestion) / QUESTIONS.length) * 100;
    const isMultiSelect = question.multiSelect;
    
    return (
      <div className="min-h-screen bg-oat px-5 py-6">
        <div className="max-w-[540px] mx-auto">
          {/* Header: back + progress indicator */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={goBack}
              className="text-ink/60 font-hanken text-sm flex items-center gap-1 hover:text-ink transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4" /> back
            </button>
            <span className="text-moss-green font-hanken text-sm">
              {currentQuestion + 1} of {QUESTIONS.length}
            </span>
          </div>
          
          {/* Progress bar */}
          <div className="h-2 bg-mushroom-lt rounded-full mb-8 overflow-hidden">
            <motion.div
              className="h-full bg-lime rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
          
          {/* Question */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentQuestion}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <h2 className="font-anton text-[30px] sm:text-[42px] leading-[1.15] text-ink lowercase mb-4">
                {question.question}
              </h2>
              
              {/* Multi-select hint */}
              {isMultiSelect && (
                <p className="font-hanken text-sm text-ink/60 mb-6">choose as many as apply</p>
              )}
              
              {/* Answer options */}
              <div className="space-y-3">
                {question.answers.map((answer, index) => {
                  const letter = String.fromCharCode(97 + index); // a, b, c, d, e
                  const isSelected = isMultiSelect 
                    ? multiSelectAnswers.includes(answer.value)
                    : selectedAnswer === answer.value;
                  
                  return (
                    <button
                      key={answer.value}
                      onClick={() => handleAnswer(answer.value)}
                      className={`w-full flex items-start gap-4 p-4 rounded-2xl border transition-all text-left ${
                        isSelected
                          ? 'bg-lime border-lime'
                          : 'bg-white border-mushroom-lt hover:border-lime/50'
                      }`}
                      data-testid={`answer-${answer.value}`}
                    >
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-hanken font-bold flex-shrink-0 ${
                        isSelected ? 'bg-ink-deep text-lime' : 'bg-lime text-ink-deep'
                      }`}>
                        {isMultiSelect && isSelected ? <Check className="w-4 h-4" /> : letter}
                      </span>
                      <span className={`font-hanken text-[17px] leading-snug ${
                        isSelected ? 'text-ink-deep' : 'text-ink'
                      }`}>
                        {answer.label}
                      </span>
                    </button>
                  );
                })}
              </div>
              
              {/* Continue button for multi-select */}
              {isMultiSelect && (
                <button
                  onClick={handleMultiSelectContinue}
                  disabled={multiSelectAnswers.length === 0}
                  className={`w-full mt-6 font-hanken font-bold text-lg py-4 rounded-full transition-all ${
                    multiSelectAnswers.length > 0
                      ? 'bg-ink text-oat hover:bg-ink/90'
                      : 'bg-mushroom-lt text-ink/40 cursor-not-allowed'
                  }`}
                  data-testid="continue-btn"
                >
                  continue <ArrowRight className="inline-block w-5 h-5 ml-1" />
                </button>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    );
  };

  // Render email screen
  const renderEmail = () => (
    <div className="min-h-screen bg-oat relative overflow-hidden">
      {/* Soft lime glows */}
      <div className="absolute top-0 right-0 w-80 h-80 bg-lime/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-lime/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
      
      <div className="relative z-10 min-h-screen flex items-center justify-center px-5 py-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-lg p-8 pt-9 max-w-[460px] w-full"
        >
          {/* Eyebrow */}
          <p className="text-moss-green text-xs uppercase tracking-wider mb-4 font-hanken">
            almost there ✦
          </p>
          
          {/* Headline */}
          <h1 className="font-anton text-[34px] sm:text-[46px] leading-[1.1] text-ink lowercase mb-8">
            where should i send{' '}
            <span className="bg-lime text-ink-deep px-2 py-0.5 rounded-md inline-block">
              my take?
            </span>
          </h1>
          
          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="first name"
              className="w-full px-4 py-3.5 rounded-xl border border-mushroom-lt bg-white font-hanken text-ink placeholder:text-ink/40 focus:outline-none focus:border-lime transition-colors"
              data-testid="first-name-input"
            />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email"
              className="w-full px-4 py-3.5 rounded-xl border border-mushroom-lt bg-white font-hanken text-ink placeholder:text-ink/40 focus:outline-none focus:border-lime transition-colors"
              data-testid="email-input"
            />
            
            {error && (
              <p className="text-red-500 font-hanken text-sm">{error}</p>
            )}
            
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-lime text-ink-deep font-hanken font-bold text-lg py-4 rounded-full hover:bg-lime/90 transition-all disabled:opacity-60"
              data-testid="submit-btn"
            >
              {isSubmitting ? 'sending...' : 'show me'} <ArrowRight className="inline-block w-5 h-5 ml-1" />
            </button>
            
            <p className="text-ink/50 font-hanken text-xs text-center">
              you'll also get my emails. unsubscribe anytime.
            </p>
          </form>
        </motion.div>
      </div>
    </div>
  );

  // Render result screen
  const renderResult = () => {
    if (!result) return null;
    
    const { take, answers } = result;
    const whyParagraph = buildWhyParagraph(take, answers, config);
    const wantLine = getWantLine(answers.want, take, config.wantLines, config.wantAdditions);
    const cardDescriptions = config.cardDescriptions[answers.know] || config.cardDescriptions.all;
    const cardOrder = config.cardOrders[take] || ['talk', 'howItWorks', 'products'];
    
    // Card definitions
    const cards = {
      talk: {
        title: "let's talk about what's possible",
        description: cardDescriptions.talk,
        buttonText: config.talkCard.buttonText,
        buttonLink: config.talkCard.link,
        ctaType: config.talkCard.ctaType,
        newTab: config.talkCard.newTab,
      },
      howItWorks: {
        title: config.howItWorksTitle,
        description: cardDescriptions.howItWorks,
        buttonText: "see how it works",
        buttonLink: config.links.howItWorks,
        ctaType: 'run_it_page',
      },
      products: {
        title: "start with the products",
        description: cardDescriptions.products,
        buttonText: config.productsButtonText,
        buttonLink: config.links.tryProducts,
        ctaType: 'try_products',
      },
    };
    
    // Bold first line based on take
    const boldFirstLines = {
      not_now: "honestly? i don't think this is the right fit right now.",
      products_first: "i wouldn't tell you to jump into this yet. i'd start by understanding the products & the company.",
      full_model: "i think you'd want to see the full business model before deciding whether this fits.",
      closer_look: "i think this is worth a closer look.",
    };
    
    // Get video data based on know answer
    const videoData = answers.know !== 'all' ? VIDEOS[answers.know] : null;
    const showAllVideos = answers.know === 'all';
    
    // Single video component
    const SingleVideo = ({ video, showTitle = false }) => (
      <div className="mt-[18px] mb-6">
        {showTitle && (
          <p className="font-hanken font-bold text-[15px] text-ink mb-3">{video.title}</p>
        )}
        <div 
          className="w-full rounded-[18px] overflow-hidden bg-ink-deep"
          style={{ aspectRatio: '1280/658' }}
        >
          <iframe
            src={video.src}
            title={video.title}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        <p className="text-[13px] text-ink/60 text-center mt-3 font-hanken">
          {video.caption}
          {video.captionLink && (
            <>
              {' '}
              <a 
                href={video.captionLink.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="underline"
              >
                {video.captionLink.text}
              </a>
            </>
          )}
        </p>
      </div>
    );
    
    // All videos component (for "all of the above" answer)
    const AllVideos = () => (
      <div className="mt-[18px] mb-6">
        {ALL_VIDEOS_ORDER.map((key) => (
          <SingleVideo key={key} video={VIDEOS[key]} showTitle={true} />
        ))}
        <p className="text-center font-hanken text-[15px] text-ink mt-6">
          want the whole thing?{' '}
          <a 
            href={config.allVideosLine.url}
            target="_blank"
            rel="noopener noreferrer"
            className="underline font-medium"
          >
            {config.allVideosLine.text}
          </a>
        </p>
      </div>
    );
    
    return (
      <div className="min-h-screen bg-oat px-5 py-10">
        <div className="max-w-[540px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Eyebrow */}
            <p className="text-moss-green text-xs uppercase tracking-wider mb-3 font-hanken">
              based on your answers
            </p>
            
            {/* Headline */}
            <h1 className="font-anton text-[40px] sm:text-[56px] leading-[1.1] text-ink lowercase mb-8">
              okay, here's{' '}
              <span className="bg-lime text-ink-deep px-2 py-0.5 rounded-md inline-block">
                my take
              </span>
            </h1>
            
            {/* Bold first line */}
            <p className="font-hanken text-[18px] text-ink font-bold mb-4 leading-relaxed">
              {boldFirstLines[take]}
            </p>
            
            {/* Why paragraph */}
            <p className="font-hanken text-[18px] text-ink mb-6 leading-relaxed" data-testid="result-why">
              {whyParagraph}
            </p>
            
            {/* Video for products_first - right after why paragraph */}
            {take === 'products_first' && (
              <>
                <p className="font-hanken text-[18px] text-ink mb-2 leading-relaxed">
                  here's me on {videoData ? videoData.phrase : VIDEOS.company.phrase}.
                </p>
                {showAllVideos ? <AllVideos /> : <SingleVideo video={videoData || VIDEOS.company} />}
              </>
            )}
            
            {/* Before you decide (only for closer_look and full_model) */}
            {(take === 'closer_look' || take === 'full_model') && (
              <>
                <p className="font-hanken text-[18px] text-ink mb-2 leading-relaxed">
                  {showAllVideos 
                    ? config.allVideosIntro
                    : `${buildBeforeYouDecide(answers.know, VIDEOS).replace('.', ',')} so here's me explaining it.`
                  }
                </p>
                
                {/* Video(s) */}
                {showAllVideos ? <AllVideos /> : <SingleVideo video={videoData} />}
                
                <p className="font-hanken text-[18px] text-ink mb-6 leading-relaxed">
                  {config.teamLine}
                </p>
                
                {/* Team photo */}
                <div className="mb-6 rounded-2xl overflow-hidden">
                  <img
                    src={IMAGES.team}
                    alt="Kristen's team"
                    className="w-full aspect-[4/5] object-cover"
                  />
                </div>
              </>
            )}
            
            {/* Want line */}
            <div className="bg-lime rounded-xl p-4 mb-8">
              <p className="font-hanken font-bold text-[17px] text-ink-deep" data-testid="result-want">
                {wantLine.text} {wantLine.emoji}
              </p>
            </div>
            
            {/* Next steps */}
            {take !== 'not_now' ? (
              <>
                <h2 className="font-anton text-[28px] sm:text-[36px] text-ink lowercase mb-6">
                  here's what i'd look at next
                </h2>
                
                <div className="space-y-4 mb-6">
                  {cardOrder.map((cardKey, index) => {
                    const card = cards[cardKey];
                    const isFirst = index === 0;
                    
                    return (
                      <div
                        key={cardKey}
                        data-testid={`card-${cardKey}`}
                        className={`rounded-2xl p-5 ${isFirst ? 'bg-lime' : 'bg-white border border-mushroom-lt'}`}
                      >
                        {isFirst && (
                          <span className="inline-block bg-white text-ink-deep font-hanken text-xs uppercase tracking-wider px-3 py-1 rounded-full mb-3">
                            where i'd start
                          </span>
                        )}
                        <h3 className={`font-hanken font-bold text-lg mb-2 ${isFirst ? 'text-ink-deep' : 'text-ink'}`}>
                          {card.title}
                        </h3>
                        <p className={`font-hanken text-[15px] mb-4 ${isFirst ? 'text-ink-deep/80' : 'text-ink/70'}`}>
                          {card.description}
                        </p>
                        {isFirst ? (
                          <a
                            href={card.buttonLink}
                            data-testid={`card-${cardKey}-btn`}
                            {...(card.newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                            onClick={() => trackCTAClick(card.ctaType)}
                            className="inline-block bg-ink text-oat font-hanken font-bold text-base py-3 px-6 rounded-full hover:bg-ink/90 transition-all"
                          >
                            {card.buttonText} <ArrowRight className="inline-block w-4 h-4 ml-1" />
                          </a>
                        ) : (
                          <a
                            href={card.buttonLink}
                            data-testid={`card-${cardKey}-btn`}
                            {...(card.newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                            onClick={() => trackCTAClick(card.ctaType)}
                            className="font-hanken font-medium text-ink underline underline-offset-2 hover:text-ink/70 transition-colors"
                          >
                            {card.buttonText} →
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {/* Not ready line */}
                <p className="text-center font-hanken text-sm text-ink/60 mb-10">
                  not ready for any of it?{' '}
                  <a
                    href={LINKS.instagram}
                    onClick={() => trackCTAClick('instagram')}
                    className="font-bold underline underline-offset-2 hover:text-ink transition-colors"
                  >
                    follow along on instagram
                  </a>
                  {' '}or{' '}
                  <a
                    href={SMS_CONSULT}
                    onClick={() => trackCTAClick('consult_sms')}
                    className="font-bold underline underline-offset-2 hover:text-ink transition-colors"
                  >
                    text me CONSULT
                  </a>
                  {' '}to just talk hair.
                </p>
              </>
            ) : (
              <>
                {/* not_now: only instagram + products */}
                <h2 className="font-anton text-[28px] sm:text-[36px] text-ink lowercase mb-6">
                  if that changes
                </h2>
                
                <div className="space-y-4 mb-6">
                  {/* Instagram card (lime) */}
                  <div className="bg-lime rounded-2xl p-5">
                    <h3 className="font-hanken font-bold text-lg text-ink-deep mb-2">
                      follow along on instagram
                    </h3>
                    <p className="font-hanken text-[15px] text-ink-deep/80 mb-4">
                      no pitch. you'll see what i'm building & you can come back to this anytime.
                    </p>
                    <a
                      href={LINKS.instagram}
                      onClick={() => trackCTAClick('instagram')}
                      className="inline-block bg-ink text-oat font-hanken font-bold text-base py-3 px-6 rounded-full hover:bg-ink/90 transition-all"
                    >
                      follow @iamkristenhinman
                    </a>
                  </div>
                  
                  {/* Products card (white) */}
                  <div className="bg-white border border-mushroom-lt rounded-2xl p-5">
                    <h3 className="font-hanken font-bold text-lg text-ink mb-2">
                      {cards.products.title}
                    </h3>
                    <p className="font-hanken text-[15px] text-ink/70 mb-4">
                      {cards.products.description}
                    </p>
                    <a
                      href={cards.products.buttonLink}
                      onClick={() => trackCTAClick('try_products')}
                      className="font-hanken font-medium text-ink underline underline-offset-2 hover:text-ink/70 transition-colors"
                    >
                      {cards.products.buttonText} →
                    </a>
                  </div>
                </div>
              </>
            )}
            
            {/* Footer links */}
            <div className="flex items-center justify-center gap-6 mb-6">
              <button
                onClick={retakeQuiz}
                data-testid="retake-btn"
                className="font-hanken text-sm text-ink/60 underline underline-offset-2 hover:text-ink transition-colors flex items-center gap-1"
              >
                <RotateCcw className="w-3.5 h-3.5" /> retake the quiz
              </button>
              <button
                onClick={handleShare}
                data-testid="share-btn"
                className="font-hanken text-sm text-ink/60 underline underline-offset-2 hover:text-ink transition-colors flex items-center gap-1"
              >
                <Share2 className="w-3.5 h-3.5" /> {copied ? 'copied!' : 'send to a friend'}
              </button>
            </div>
            
            {/* Fine print */}
            <p className="text-center font-hanken text-[11.5px] text-ink/50 leading-relaxed">
              this is my take based on the answers you picked. i'm an independent monat market partner. income isn't guaranteed. see the{' '}
              <a href={LINKS.incomeDisclosure} target="_blank" rel="noopener noreferrer" className="underline">
                income disclosure statement
              </a>
              . product results vary.{config.finePrintExtra}
            </p>
          </motion.div>
        </div>
      </div>
    );
  };

  return (
    <>
      <AnimatePresence mode="wait">
        {screen === 'intro' && renderIntro()}
        {screen === 'quiz' && renderQuiz()}
        {screen === 'email' && renderEmail()}
        {screen === 'result' && renderResult()}
      </AnimatePresence>
    </>
  );
}
